import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

/**
 * Reusable empty-state placeholder shown when a list/page has no records.
 *
 * Props:
 *  - icon: a Lucide icon component (already imported by the caller).
 *  - title: short headline (~5 words).
 *  - description: one or two sentences explaining what users can do.
 *  - actionLabel + actionTo (optional): renders a primary CTA <Link/Button>.
 *  - secondaryLabel + secondaryOnClick (optional): renders a ghost button.
 *  - testid (optional): data-testid for the wrapper.
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  actionLabel,
  actionTo,
  actionOnClick,
  secondaryLabel,
  secondaryOnClick,
  testid,
}) {
  return (
    <div
      data-testid={testid || "empty-state"}
      className="empty-state-card rounded-sm p-10 sm:p-14 text-center fade-in"
    >
      <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#EEF2FF] text-[#002FA7]">
        {Icon ? <Icon className="h-6 w-6" /> : null}
      </div>
      <h3
        className="text-lg sm:text-xl font-bold text-slate-900 mb-2"
        style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
      >
        {title}
      </h3>
      <p className="text-sm text-slate-500 max-w-md mx-auto mb-6 leading-relaxed">
        {description}
      </p>
      {(actionLabel || secondaryLabel) && (
        <div className="flex items-center justify-center gap-2 flex-wrap">
          {actionLabel && actionTo && (
            <Button
              asChild
              className="rounded-sm bg-[#002FA7] hover:bg-[#002080]"
              data-testid={`${testid || "empty-state"}-primary-action`}
            >
              <Link to={actionTo}>{actionLabel}</Link>
            </Button>
          )}
          {actionLabel && !actionTo && actionOnClick && (
            <Button
              onClick={actionOnClick}
              className="rounded-sm bg-[#002FA7] hover:bg-[#002080]"
              data-testid={`${testid || "empty-state"}-primary-action`}
            >
              {actionLabel}
            </Button>
          )}
          {secondaryLabel && secondaryOnClick && (
            <Button
              variant="ghost"
              onClick={secondaryOnClick}
              className="rounded-sm"
              data-testid={`${testid || "empty-state"}-secondary-action`}
            >
              {secondaryLabel}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
